'use strict';
require('public/js/elements/overlay-spinner');
const $onReady = require('./lib/on-ready');
const get = require('lodash/get');
const paypal = require('public/js/components/paypal');
import paypalSmartButtons from './components/paypal-smart-buttons';
import applePayButton from './components/apple-pay-button';
import googlePayButton from './components/google-pay-button';
const stripeExpressCheckoutModule = require('public/js/components/stripe-express-checkout/index');
const stripeExpressCheckout = stripeExpressCheckoutModule.default;
const { isRpPaymentsExpressCheckoutEligible } = stripeExpressCheckoutModule;
const utils = require('public/js/helpers/utils');
const { cartCache } = require('./components/cart');
const { PAYLATER_MINIMUM_ELIGIBLE_AMOUNT } = require('./components/paypal-smart-buttons/paypal-constants');
const { PRODUCT_QUANTITY } = require('public/js/constants/product_quantity');
const {
	SHIPPING_QUOTE_CLEARED_EVENT,
	clearCachedShippingQuote,
} = require('public/js/lib/shipping-quote-cache');

const {
	trackCheckoutEvent,
	trackCartRemovalEvent,
	trackClearCartEvent,
	trackCartAddEvent
} = require('public/js/lib/adobe-dtm-helper');

require('public/css/cart.scss');

const cart = {};

cart._cartUpdateInProgress = false;
cart._cartUpdateNeedsRetry = false;

const removeCartItemById = (cartItems, removeItemId) => {
	if (!Array.isArray(cartItems) || cartItems.length === 0) {
		return [];
	}

	return cartItems.filter(cartItem => String(cartItem.id) !== String(removeItemId));
};

const calculateItemPriceInCart = (item) => {
	const itemPrice = item?.price || 0;
	const itemQuantity = item?.quantity || 0;

	return itemPrice * itemQuantity;
};

cart.parseCartAmount = function (amount) {
	if (typeof amount === 'number') {
		return Number.isFinite(amount) ? amount : 0;
	}

	const numericAmount = Number(amount);
	if (Number.isFinite(numericAmount)) {
		return numericAmount;
	}

	if (typeof amount !== 'string') {
		return 0;
	}

	const normalizedAmount = amount.trim().replace(/\s/g, '').replace(/[^0-9,.-]/g, '');
	if (normalizedAmount === '') {
		return 0;
	}

	const lastCommaIndex = normalizedAmount.lastIndexOf(',');
	const lastDotIndex = normalizedAmount.lastIndexOf('.');
	const commaCount = (normalizedAmount.match(/,/g) || []).length;
	const dotCount = (normalizedAmount.match(/\./g) || []).length;
	let decimalNormalizedAmount = normalizedAmount;

	if (commaCount > 0 && dotCount > 0) {
		const decimalSeparator = lastCommaIndex > lastDotIndex ? ',' : '.';
		decimalNormalizedAmount = decimalSeparator === ','
			? normalizedAmount.replace(/\./g, '').replace(',', '.')
			: normalizedAmount.replace(/,/g, '');
	} else if (commaCount > 0) {
		const digitsAfterComma = normalizedAmount.length - lastCommaIndex - 1;
		decimalNormalizedAmount = commaCount === 1 && digitsAfterComma !== 3
			? normalizedAmount.replace(',', '.')
			: normalizedAmount.replace(/,/g, '');
	}
	const parsedAmount = Number.parseFloat(decimalNormalizedAmount);

	return Number.isFinite(parsedAmount) ? parsedAmount : 0;
};

cart.getCartSubtotalAmount = function ($subtotalModule, $subtotalValue) {
	const rawSubtotal = $subtotalModule.length > 0 ? $subtotalModule.data('cart-subtotal') : undefined;
	const numericSubtotal = Number(rawSubtotal);
	if (Number.isFinite(numericSubtotal)) {
		return numericSubtotal;
	}

	const subtotalDisplayText = $subtotalValue.length > 0 ? $subtotalValue.text() : '0';
	return cart.parseCartAmount(subtotalDisplayText);
};

/**
 * Computes the payable cart total (subtotal + shipping) for wallet button labels.
 *
 * `subtotalAmount` is already net of item sale prices. The cart "savings" figure is an
 * informational MSRP markdown that the server never subtracts from the estimated total
 * (see `cart_subtotal/data.php`), so it must not be subtracted here either.
 *
 * @param {{ subtotalAmount?: (number|string) }} [data] Optional totals payload from
 *   `/ajax/cart/get-totals`; when omitted, reads current DOM state.
 * @returns {number|null} Positive estimated total, or null when inputs are invalid.
 */
cart.computeExpressCheckoutEstimatedTotal = function (data) {
	const $subtotalModule = rp$('.cart-subtotal-module');
	const $subtotalValue = rp$('.cart-amount-subtotal .order-summary-value');
	const subtotal = data?.subtotalAmount === undefined
		? cart.getCartSubtotalAmount($subtotalModule, $subtotalValue)
		: Number.parseFloat(data.subtotalAmount);

	if (!Number.isFinite(subtotal) || subtotal <= 0) {
		return null;
	}

	const shipping = cart.currentShippingAmount > 0 ? cart.currentShippingAmount : 0;
	const estimatedTotal = subtotal + shipping;

	if (!Number.isFinite(estimatedTotal) || estimatedTotal <= 0) {
		return null;
	}

	return estimatedTotal;
};

const cartDomQuantitiesMatchSnapshot = function (domItems, savedSnapshot) {
	if (domItems === null || !Array.isArray(savedSnapshot)) {
		return false;
	}
	const domById = new Map();
	domItems.forEach(function CartDomByIdIterator(item) {
		domById.set(String(item.id), item);
	});
	for (const snap of savedSnapshot) {
		const domItem = domById.get(String(snap.id));
		if (!domItem) {
			return false;
		}
		if (Number(domItem.quantity) !== Number(snap.quantity)) {
			return false;
		}
	}
	return true;
};

cart.removeItem = function (evt) {
	evt.preventDefault();
	const $link = rp$(this);

	const confirmMessage = $link.data('confirm') || window.javascriptContent.are_you_sure_confirmation;
	const confirmation = window.confirm(confirmMessage);
	if (!confirmation) { return; }
	const $remainingItemsBefore = rp$('.cart-items-list').find('.cart-item-card, .cart-item, .cart-listing-item, .cart-row, tr');
	const isLastItem = $remainingItemsBefore.length === 1;

	const data = {
		id: $link.data('id'),
		sku: $link.data('product-id'),
		catalog: $link.data('catalog')
	};

	$ajax({
		method: 'POST',
		dataType: 'json',
		url: '/ajax/cart/remove',
		data: data,
		success: (response, status/*, xhr*/) => {
			if (status === 'success') {
				// Send remove from cart tracking events
				const productDetails = cart.getProductDetailsFromCartElement($link);
				rp_app.track.track_ec_cart_event('remove', productDetails);

				if (isLastItem) {
					window.rp_app?.invalidateSupplierCache?.();
				}

				if (get(window, 'digitalData.newEvent')) {
					let cartTotalPrice = 0;

					if (window.digitalData.products.cart) {
						const cartItems = removeCartItemById(window.digitalData.products.cart, productDetails.id);
						cartItems.forEach(item => {
							cartTotalPrice += calculateItemPriceInCart(item);
						});
					}
					window.digitalData.page.pageInfo.totalPrice = cartTotalPrice.toFixed(2);

					if (window.digitalData.cartItems) {
						window.digitalData.cartItems -= productDetails.quantity;
					}

					cart.sendAdobeDTMCartRemovalEvent(productDetails);
				}

				clearCachedShippingQuote();
				if (isLastItem) {
					cartCache.clear();
					rp_app.location.reload();
					return;
				}

				const $cartItemContainer = $link.closest('.cart-item-card, .cart-item, .cart-listing-item, .cart-row, tr');
				if ($cartItemContainer.length > 0) {
					$cartItemContainer.remove();
				}

				rp$(document).trigger(SHIPPING_QUOTE_CLEARED_EVENT);

				cart.showCartTotalsSpinner();
				cart.updateCartTotals();

				if (window.rp_app?.cart?.load_cart) {
					window.rp_app.cart.load_cart();
				}
			}
		}
	});
	cartCache.clear();

};

cart.downloadCSVErrors = function (evt) {
	evt.preventDefault();
	function downloadCSV(data) {
		let csvContent = '';
		csvContent += `"MPN","Brand","QTY","Details","Filename"\n`;
		for (const fileData of data) {
			const fileName = Object.keys(fileData)[0];
			const errorObjects = fileData[fileName];
			const fileCSVContent = errorObjects.map(item => {
				const { mpn, brand, qty, details } = item;
				return `"${mpn}","${brand}","${qty}","${details}","${fileName}"`;
			}).join('\n');

			csvContent += `${fileCSVContent}\n`;
		}

		if (!csvContent.length) {
			return;
		}

		const now = new Date();
		const year = now.getUTCFullYear();
		const month = String(now.getUTCMonth() + 1).padStart(2, '0');
		const day = String(now.getUTCDate()).padStart(2, '0');
		const hours = String(now.getUTCHours()).padStart(2, '0');
		const minutes = String(now.getUTCMinutes()).padStart(2, '0');

		const formattedDateTime = `${year}${month}${day}_${hours}${minutes}_UTC`;

		const blob = new Blob([csvContent], { type: 'text/csv' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = 'errors_'+formattedDateTime+'.csv';
		link.click();
		URL.revokeObjectURL(url);
	}
	let errorArray = JSON.parse(sessionStorage.getItem('Error')) || [];
	
	downloadCSV(errorArray);

};

cart.emptyCart = function (evt) {
	evt.preventDefault();
	const $link = rp$(this);
	const confirmMessage = $link.data('confirm') || window.javascriptContent.are_you_sure_confirmation;
	const confirmation = window.confirm(confirmMessage);
	if (!confirmation) { return; }

	$ajax({
		method: 'POST',
		dataType: 'json',
		url: '/ajax/cart/empty',
		success: (response, status/*, xhr*/) => {
			if (status === 'success') {
				sessionStorage.removeItem('Error');
				window.rp_app?.invalidateSupplierCache?.();
				// Get all item removal links so we can get the data we need
				const $removeLinks = rp$('.remove-link .remove-item-link');
				const productsToRemove = rp$.map($removeLinks, (link) => {
					return cart.getProductDetailsFromCartElement(rp$(link));
				});
				// Track cart remove analytics events
				rp_app.track.track_ec_cart_event('remove', productsToRemove);

				if (get(window, 'digitalData.newEvent')) {
					window.digitalData.cartItems = 0;
					window.digitalData.page.pageInfo.totalPrice = 0;
					trackClearCartEvent(productsToRemove);
				}
				rp_app.location.reload();
			}
		}
	});
	cartCache.clear();
	clearCachedShippingQuote();

};

cart.formatCartPrice = function (amount, templateText) {
	const numericAmount = Number(amount);
	if (Number.isNaN(numericAmount)) {
		return typeof templateText === 'string' ? templateText : '';
	}
	const currencyCode = globalThis.rp_app?.cart?._currency_code || 'USD';
	const formatter = globalThis.rp_app?.priceFormat;
	return typeof formatter === 'function' ? formatter(numericAmount, currencyCode) : String(numericAmount);
};

cart.showLineItemSpinner = function (itemIds) {
	if (!itemIds) {
		return;
	}

	const ids = Array.isArray(itemIds) ? itemIds : [itemIds];

	ids.forEach((itemId) => {
		if (!itemId) {
			return;
		}

		const $input = rp$('.update-cart-quantity[data-id="' + itemId + '"]');
		if ($input.length === 0) {
			return;
		}

		const $card = $input.closest('.cart-item-card');
		if ($card.length === 0) {
			return;
		}

		const $totalEl = $card.find('.cart-item-total-amount');
		if ($totalEl.length === 0) {
			return;
		}

		if (!$totalEl.data('spinner-shown')) {
			// Save original text if not already saved
			if (!$totalEl.data('original-text')) {
				$totalEl.data('original-text', $totalEl.text());
			}
			$totalEl.data('spinner-shown', true);
			$totalEl.html('<i class="fa fa-spinner fa-spin"></i>');
		}
	});
};

cart.updateLineItemTotals = function (items) {
	if (!Array.isArray(items) || items.length === 0) {
		return;
	}

	items.forEach(function CartUpdateLineTotals_Iterator(item) {
		const itemId = item?.id;
		const quantity = Number(item?.quantity);
		const unitPrice = Number(item?.price);

		if (!itemId || Number.isNaN(quantity) || Number.isNaN(unitPrice)) {
			return;
		}

		const $input = rp$('.update-cart-quantity[data-id="' + itemId + '"]');
		if ($input.length === 0) {
			return;
		}

		$input.attr('value', quantity);
		$input.val(quantity);
		
		const $decreaseBtn = $input.siblings('.btn-quantity-decrease, .quantity-btn.minus');
		if ($decreaseBtn.length > 0) {
			if (quantity === 1) {
				$decreaseBtn.attr('data-quantity-one', 'true');
			} else {
				$decreaseBtn.removeAttr('data-quantity-one');
			}
		}

		const $card = $input.closest('.cart-item-card');
		if ($card.length === 0) {
			return;
		}

		const $totalEl = $card.find('.cart-item-total-amount');

		if ($totalEl.length === 0) {
			return;
		}

		// Save original text if not already saved
		if (!$totalEl.data('original-text')) {
			$totalEl.data('original-text', $totalEl.text());
		}

		const lineTotal = unitPrice * quantity;
		const originalTotalText = $totalEl.data('original-text') || $totalEl.text();
		const templateText = originalTotalText;
		const formattedTotal = cart.formatCartPrice(lineTotal, templateText);

		$totalEl.text(formattedTotal);
		$totalEl.removeData('spinner-shown');

		const $unitPriceEl = $card.find('.cart-item-total-unit-price');
		const $unitPriceValueEl = $card.find('.cart-item-total-unit-price-value');
		if ($unitPriceEl.length > 0) {
			if (quantity > 1) {
				$unitPriceEl.removeClass('cart-item-unit-price-hidden');
				if ($unitPriceValueEl.length > 0) {
					const unitPriceTemplate = $unitPriceValueEl.text();
					$unitPriceValueEl.text(cart.formatCartPrice(unitPrice, unitPriceTemplate));
				}
			} else {
				$unitPriceEl.addClass('cart-item-unit-price-hidden');
			}
		}
	});
};



/* Collect update data
.*-----------------------------------------*/
cart.collectCartUpdateItemsFromDom = function () {
	const items = [];
	rp$('.update-cart-quantity').each(function CartCollectItemsIterator(idx, input) {
		const $input = rp$(input);
		items.push({
			id: $input.data('id'),
			sku: $input.data('product-id'),
			catalog: $input.data('catalog'),
			quantity: $input.val(),
			initialQuantity: $input.data('initial-quantity'),
			price: $input.data('price'),
			brand: $input.data('brand'),
			name: $input.data('name'),
			skuStripped: String($input.data('sku-stripped')),
			group: $input.data('group') || '',
		});
	});

	/* Validate Quanity
    .*-----------------------------------------*/
	let valid = true;
	items.forEach(function CartCollectValidationIterator(item) {
		const qty = Number.parseInt(item.quantity, 10);
		if (isNaN(qty) || qty < 0) {
			valid = false;
		} else {
			item.quantity = qty;
		}
	});
	if (valid === false) {
		return null;
	}
	return items;
};

cart.syncCartInputsInitialQuantityFromSavedItems = function (savedItems) {
	if (!Array.isArray(savedItems)) {
		return;
	}
	savedItems.forEach(function CartSyncInitialIterator(item) {
		if (!item.id) {
			return;
		}
		const $input = rp$('.update-cart-quantity[data-id="' + item.id + '"]');
		if ($input.length === 0) {
			return;
		}
		$input.data('initial-quantity', item.quantity);
	});
};

cart.updateCart = function (evt) {
	evt.preventDefault();
	const $link = rp$(this);

	const items = cart.collectCartUpdateItemsFromDom();
	if (items === null) {
		const invalidMessage = $link.data('invalid-update-message') || 'Invalid Quantities!';
		window.alert(invalidMessage);
		return;
	}

	/* Update Quantities
    .*-----------------------------------------*/
	if (cart._cartUpdateInProgress) {
		cart._cartUpdateNeedsRetry = true;
		return;
	}
	cart._cartUpdateInProgress = true;

	const changedItemIds = items
		.filter(function CartChangedItemsFilter(item) {
			return item.quantity !== item.initialQuantity;
		})
		.map(function CartChangedItemsMap(item) {
			return item.id;
		});

	if (changedItemIds.length > 0) {
		cart.showLineItemSpinner(changedItemIds);
	}

	cart.showCartTotalsSpinner();

	const savedSnapshot = items.map(function CartSnapshotIterator(item) {
		return { ...item };
	});

	$ajax({
		method: 'POST',
		dataType: 'json',
		url: '/ajax/cart/update-cart',
		data: {
			items: items
		},
		success: (response, status/*, xhr*/) => {
			if (status === 'success') {
				const domItems = cart.collectCartUpdateItemsFromDom();
				const domMatchesRequest = cartDomQuantitiesMatchSnapshot(domItems, savedSnapshot);
				if (domMatchesRequest || domItems === null) {
					cart.updateLineItemTotals(savedSnapshot);
					cart.syncCartInputsInitialQuantityFromSavedItems(savedSnapshot);
				} else {
					cart.updateLineItemTotals(domItems);
					cart.syncCartInputsInitialQuantityFromSavedItems(savedSnapshot);
					cart._cartUpdateNeedsRetry = true;
				}
				cart.updateCartTotals();

				const removedItems = [];
				const addedItems = [];
				var $addToCartBtn = rp$('.add-to-cart');
				let cartTotalPrice = 0;
				const itemsForAnalytics = savedSnapshot.map(function CartAnalyticsCopyIterator(item) {
					return { ...item };
				});
				itemsForAnalytics.forEach(function CartUpdateEventType_Iterator(item) {
					cartTotalPrice += calculateItemPriceInCart(item);
					if (item.quantity < item.initialQuantity && item.quantity !== item.initialQuantity) {
						item.quantity = item.initialQuantity - item.quantity;
						removedItems.push(item);
					} else if (item.quantity > item.initialQuantity && item.quantity !== item.initialQuantity) {
						item.quantity = item.quantity - item.initialQuantity;
						addedItems.push(item);
					}
				});

				if (get(window, 'digitalData.newEvent') && window.digitalData.page.pageInfo.totalPrice) {
					window.digitalData.page.pageInfo.totalPrice = cartTotalPrice.toFixed(2);
				}

				if (addedItems.length > 0) {
					window.rp_app.track.track_ec_general('add_to_cart', {
						currency: $addToCartBtn.data('currencyCode') || 'USD',
						items: utils.updateQuantityForGA(addedItems)
					});

					if (get(window, 'digitalData.newEvent')) {
						addedItems.forEach((product) => {
							product.id = product.skuStripped;
							cart.sendAdobeDTMCartAddEvent(product);

							if (window.digitalData.cartItems) {
								window.digitalData.cartItems += product.quantity;
							}
						});
					}
				}
				if (removedItems.length > 0) {
					if (window.tracking.google.use_gtag_admind_analytics) {
						window.rp_app.track.track_ec_general('remove_from_cart', {
							currency: $addToCartBtn.data('currencyCode') || 'USD',
							items: utils.updateQuantityForGA(removedItems)
						});
					} else {
						rp_app.track.track_ec_cart_event('remove', removedItems);
					}

					if (get(window, 'digitalData.newEvent')) {
						removedItems.forEach((product) => {
							product.id = product.skuStripped;
							cart.sendAdobeDTMCartRemovalEvent(product);

							if (window.digitalData.cartItems) {
								window.digitalData.cartItems -= product.quantity;
							}
						});
					}
				}

				if (window.rp_app?.cart?.load_cart) {
					window.rp_app.cart.load_cart();
				}
			}
		},
		complete: () => {
			cart._cartUpdateInProgress = false;
			if (cart._cartUpdateNeedsRetry) {
				cart._cartUpdateNeedsRetry = false;
				const $firstInput = rp$('.update-cart-quantity').first();
				if ($firstInput.length > 0) {
					cart.updateCart.call($firstInput[0], {
						preventDefault: function () {},
						target: $firstInput[0],
					});
				}
			}
		},
	});
	cartCache.clear();
};

cart.showCartTotalsSpinner = function () {
	const $subtotal = rp$('.cart-amount-subtotal');
	const $savings = rp$('.cart-total-savings');
	const $estimatedTotal = rp$('.estimated-total .order-summary-value');
	
	if ($subtotal.length) {
		const $subtotalValue = $subtotal.find('.order-summary-value');
		if ($subtotalValue.length > 0) {
			$subtotalValue.data('original-text', $subtotalValue.text());
			$subtotalValue.html('<i class="fa fa-spinner fa-spin"></i>');
		}
	}
	
	if ($savings.length) {
		const $savingsValue = $savings.find('.order-summary-value');
		if ($savingsValue.length > 0) {
			$savingsValue.data('original-text', $savingsValue.text());
			$savingsValue.html('<i class="fa fa-spinner fa-spin"></i>');
		}
	}
	
	if ($estimatedTotal.length > 0) {
		$estimatedTotal.data('original-text', $estimatedTotal.text());
		$estimatedTotal.html('<i class="fa fa-spinner fa-spin"></i>');
	}
};

cart.hideCartTotalsSpinner = function () {
	const $subtotal = rp$('.cart-amount-subtotal');
	const $savings = rp$('.cart-total-savings');
	const $estimatedTotal = rp$('.estimated-total .order-summary-value');
	
	if ($subtotal.length) {
		const $subtotalValue = $subtotal.find('.order-summary-value');
		if ($subtotalValue.length > 0 && $subtotalValue.data('original-text')) {
			$subtotalValue.text($subtotalValue.data('original-text'));
			$subtotalValue.removeData('original-text');
		}
	}
	
	if ($savings.length) {
		const $savingsValue = $savings.find('.order-summary-value');
		if ($savingsValue.length > 0 && $savingsValue.data('original-text')) {
			$savingsValue.text($savingsValue.data('original-text'));
			$savingsValue.removeData('original-text');
		}
	}
	
	if ($estimatedTotal.length > 0 && $estimatedTotal.data('original-text')) {
		$estimatedTotal.text($estimatedTotal.data('original-text'));
		$estimatedTotal.removeData('original-text');
	}
};

cart.parseCartTotalsResponse = function (response) {
	if (typeof response === 'string') {
		try {
			return JSON.parse(response);
		} catch (e) {
			cart.hideCartTotalsSpinner();
			return null;
		}
	}
	return response;
};

cart.updateSubtotalDisplay = function (data) {
	const rawSubtotal = Number(data.subtotalAmount);
	if (Number.isFinite(rawSubtotal)) {
		rp$('.cart-subtotal-module').data('cart-subtotal', rawSubtotal);
	}

	const value = data.subtotalAmountDisplayFormatted !== undefined
		? data.subtotalAmountDisplayFormatted
		: data.subtotalAmountFormatted;
	if (value === undefined) {
		return;
	}
	const $subtotal = rp$('.cart-amount-subtotal');
	if ($subtotal.length === 0) {
		return;
	}
	const $subtotalValue = $subtotal.find('.order-summary-value');
	if ($subtotalValue.length > 0) {
		$subtotalValue.empty().text(value);
	}
	$subtotal.removeData('original-text');
};

cart.updateSavingsDisplay = function (data) {
	const $savings = rp$('.cart-total-savings');
	const hasSavings = data.totalSavings !== undefined && Number.parseFloat(data.totalSavings) > 0;
	const shouldShowSavings = (data.showTotalSavings || hasSavings) && data.totalSavingsFormatted !== undefined;
	
	if (shouldShowSavings) {
		if ($savings.length > 0) {
			const $savingsValue = $savings.find('.order-summary-value');
			if ($savingsValue.length > 0) {
				$savingsValue.text(data.totalSavingsFormatted);
			}
			$savings.show();
			$savings.removeData('original-text');
		} else {
			const $subtotal = rp$('.cart-amount-subtotal');
			if ($subtotal.length > 0) {
				const $container = rp$('.cart-subtotal-module');
				const totalSavingsLabel = $container.length > 0 && $container.data('total-savings-label')
					? $container.data('total-savings-label')
					: 'Total Savings';
				const $newSavings = rp$('<li>')
					.addClass('order-summary-row cart-total-savings')
					.append(rp$('<span>').addClass('order-summary-label').text(totalSavingsLabel))
					.append(rp$('<span>').addClass('order-summary-value').text(data.totalSavingsFormatted));
				$subtotal.after($newSavings);
			}
		}
	} else if ($savings.length > 0 && !hasSavings && !data.showTotalSavings) {
		$savings.hide();
	}
};

cart.updatePayPalMessage = function (estimatedTotal) {
	const $paymentsMessagingModule = rp$('.payments_messaging_module');
	const rpPaymentsEnv = globalThis.rp_app?.env?.rp_payments;
	if (rpPaymentsEnv?.enabled) {
		if ($paymentsMessagingModule.length > 0) {
			$paymentsMessagingModule.hide();
		}
		return;
	}
	const $ppMessage = rp$('.pp-message');

	if ($paymentsMessagingModule.length === 0 || $ppMessage.length === 0) {
		return;
	}

	const numericAmount = cart.parseCartAmount(estimatedTotal);

	if (numericAmount < PAYLATER_MINIMUM_ELIGIBLE_AMOUNT) {
		$paymentsMessagingModule.hide();
		return;
	}

	$paymentsMessagingModule.show();

	paypalSmartButtons.initPaypalMessage({
		elemOrSelector: '.pp-message',
		amount: numericAmount,
		placement: 'cart',
		style: {
			layout: 'text',
			color: 'black',
			logo: {
				position: 'left',
				type: 'primary',
			},
		},
	});
};

cart.updateEstimatedTotalDisplay = function (data) {
	const $estimatedTotal = rp$('#estimated-total-value');
	const $mobileEstimatedTotal = rp$('#mobile-estimated-total-value');
	
	if ($estimatedTotal.length === 0 && $mobileEstimatedTotal.length === 0) {
		return;
	}
	
	let formattedTotal = '';
	
	if (data.estimatedTotalFormatted !== undefined) {
		formattedTotal = data.estimatedTotalFormatted;
	} else {
		const $shippingAmount = rp$('.shipping-quote .shipping-quote-amount');
		const $shippingWrapper = rp$('.shipping-quote .shipping-quote-amount-wrapper');
		let shippingAmount = 0;
		
		if ($shippingAmount.length > 0 && $shippingAmount.text().trim() !== '') {
			const shippingText = $shippingAmount.text().trim();
			if (shippingText.toLowerCase() !== 'free' && shippingText.toLowerCase() !== '') {
				shippingAmount = cart.currentShippingAmount > 0 ? cart.currentShippingAmount : cart.parseCartAmount(shippingText);
			}
		} else if ($shippingWrapper.length > 0 && $shippingWrapper.hasClass('display-free-shipping')) {
			shippingAmount = 0;
		}
		
		const subtotal = Number.parseFloat(data.subtotalAmount) || 0;
		const savings = Number.parseFloat(data.totalSavings) || 0;
		const tax = 0.0; // Always 0 in cart (estimated)
		const estimatedTotal = subtotal + shippingAmount + tax - savings;
		
		const templateText = data.subtotalAmountFormatted || '$0.00';
		formattedTotal = cart.formatCartPrice(estimatedTotal, templateText);
	}
	
	// Update desktop Estimated Total
	if ($estimatedTotal.length > 0) {
		$estimatedTotal.empty().text(formattedTotal);
	}
	
	// Update mobile Estimated Total
	if ($mobileEstimatedTotal.length > 0) {
		$mobileEstimatedTotal.empty().text(formattedTotal);
	}
	
	// Update PayPal message with new Estimated Total
	const estimatedTotalValue = data.estimatedTotal || formattedTotal;
	cart.updatePayPalMessage(estimatedTotalValue);
};

cart.currentShippingAmount = 0;

cart.updateEstimatedTotalWithShipping = function (shippingAmount) {
	const $subtotalModule = rp$('.cart-subtotal-module');
	const $subtotalValue = rp$('.cart-amount-subtotal .order-summary-value');
	const $estimatedTotal = rp$('#estimated-total-value');
	const $mobileEstimatedTotal = rp$('#mobile-estimated-total-value');
	
	if ($estimatedTotal.length === 0 && $mobileEstimatedTotal.length === 0) {
		return;
	}
	
	const subtotal = cart.getCartSubtotalAmount($subtotalModule, $subtotalValue);
	const shipping = cart.parseCartAmount(shippingAmount);
	cart.currentShippingAmount = shipping;

	const tax = 0.0;
	const estimatedTotal = subtotal + shipping + tax;
	
	const templateText = $subtotalValue.length > 0 ? $subtotalValue.text() : '$0.00';
	const formattedTotal = cart.formatCartPrice(estimatedTotal, templateText);
	
	// Update desktop Estimated Total
	if ($estimatedTotal.length > 0) {
		$estimatedTotal.empty().text(formattedTotal);
	}
	
	// Update mobile Estimated Total
	if ($mobileEstimatedTotal.length > 0) {
		$mobileEstimatedTotal.empty().text(formattedTotal);
	}
	
	// Update PayPal message with new Estimated Total
	cart.updatePayPalMessage(estimatedTotal);

	cart.syncExpressCheckoutAmount({ subtotalAmount: subtotal });
};

cart.updateQuantityInputs = function () {
	rp$('.update-cart-quantity').each(function() {
		const $input = rp$(this);
		const currentValue = Number.parseInt($input.val(), 10);
		$input.data('initial-quantity', currentValue);
		$input.attr('value', currentValue);
	});
};

cart.updateDigitalDataPrice = function (data) {
	if (!globalThis.digitalData?.page?.pageInfo) {
		return;
	}
	
	if (data.subtotalAmount !== undefined) {
		globalThis.digitalData.page.pageInfo.totalPrice = Number.parseFloat(data.subtotalAmount).toFixed(2);
	}
};

/**
 * Pushes the recomputed estimated total into the mounted Stripe Express Checkout Element so the wallet
 * label reflects quantity/shipping changes (the ECE is mounted once and doesn't see AJAX total updates
 * like PayPal does). No-ops when RP Payments wallets aren't mounted.
 *
 * @param {{ subtotalAmount?: (number|string) }} data Parsed totals response from `/ajax/cart/get-totals`.
 * @returns {void}
 */
cart.syncExpressCheckoutAmount = function (data) {
	const estimatedTotal = cart.computeExpressCheckoutEstimatedTotal(data);
	if (estimatedTotal === null) {
		return;
	}

	stripeExpressCheckout.updateExpressCheckoutAmount(Math.round(estimatedTotal * 100));
};

cart.updateCartTotals = function () {
	$ajax({
		method: 'GET',
		dataType: 'json',
		url: '/ajax/cart/get-totals',
		cache: false,
		success: (response, status) => {
			const data = cart.parseCartTotalsResponse(response);
			if (data === null || status !== 'success' || !data || data?.status !== 'success') {
				cart.hideCartTotalsSpinner();
				return;
			}
			
			cart.updateSubtotalDisplay(data);
			cart.updateSavingsDisplay(data);
			cart.updateEstimatedTotalDisplay(data);
			
			if (data.cartPromoDiscount !== undefined) {
				const $subtotalModule = rp$('.cart-subtotal-module');
				if ($subtotalModule.length > 0) {
					$subtotalModule.data('cart-promo-discount', data.cartPromoDiscount);
				}
			}

			if (cart.currentShippingAmount > 0) {
				cart.updateEstimatedTotalWithShipping(cart.currentShippingAmount);
			}
			cart.updateQuantityInputs();
			cart.updateDigitalDataPrice(data);
			cart.syncExpressCheckoutAmount(data);
			
			if (data.itemsCount !== undefined) {
				cart.updateCartItemCount(data.itemsCount);
			}
			
			if (data.cartPromoMessage !== undefined) {
				cart.updateCartPromoMessage(data.cartPromoMessage, data.cartPromoDisclaimer);
			}
			
			const $subtotal = rp$('.cart-amount-subtotal');
			const $savings = rp$('.cart-total-savings');
			const $estimatedTotal = rp$('.estimated-total .order-summary-value');
			if ($subtotal.length) {
				const $subtotalValue = $subtotal.find('.order-summary-value');
				if ($subtotalValue.length > 0) {
					$subtotalValue.removeData('original-text');
				}
				$subtotal.removeData('original-text');
			}
			if ($savings.length) {
				const $savingsValue = $savings.find('.order-summary-value');
				if ($savingsValue.length > 0) {
					$savingsValue.removeData('original-text');
				}
				$savings.removeData('original-text');
			}
			if ($estimatedTotal.length > 0) {
				$estimatedTotal.removeData('original-text');
			}
			
			cart.hideCartTotalsSpinner();
		},
		error: () => {
			cart.hideCartTotalsSpinner();
		}
	});
};

cart.getProductDetailsFromCartElement = function ($element) {
	return {
		id: $element.data('sku-stripped'),
		name: $element.data('name'),
		brand: $element.data('brand'),
		price: Number($element.data('price')) || null,
		quantity: Number($element.data('quantity')) || 1,
		group: $element.data('group') || '',
	};
};

cart.updateCartItemCount = function (itemCount) {
	const itemCountInt = Number.parseInt(itemCount, 10) || 0;
	
	const $cartItemsCount = rp$('.cart-items-count');
	if ($cartItemsCount.length > 0) {
		const itemLabel = itemCountInt === 1 ? 'Item' : 'Items';
		$cartItemsCount.text(`(${itemCountInt} ${itemLabel})`);
	}
	
	const $cartItems = rp$('.cart-items');
	if ($cartItems.length > 0) {
		$cartItems.html(itemCountInt);
		if (itemCountInt > 0) {
			$cartItems.addClass('cart-items-exist');
		} else {
			$cartItems.removeClass('cart-items-exist');
		}
	}
	
	const $cartItemsLabel = rp$('.cart-items-label');
	if ($cartItemsLabel.length > 0) {
		const itemLabel = itemCountInt === 1 ? 'item' : 'items';
		$cartItemsLabel.html(itemLabel);
	}

	// Checkout validation compares data-cart-items-count to live /ajax/cart.
	// When quantity limits apply, sync the guard from visible qty inputs, not
	// get-totals itemsCount (which can disagree with filtered /ajax/cart).
	let guardCount = itemCountInt;
	if (rp_app?.cart?._can_apply_cart_quantity_limit) {
		const renderedCount = cart.getRenderedCartItemsCountFromDom();
		if (renderedCount === null) {
			return;
		}
		guardCount = renderedCount;
	}

	const $cartListingWithQuantityLimit = rp$(
		'.cart-listing[data-can-apply-cart-quantity-limit], ' +
		'.cart-listing-module[data-can-apply-cart-quantity-limit]'
	);
	if ($cartListingWithQuantityLimit.length > 0) {
		$cartListingWithQuantityLimit.attr('data-cart-items-count', guardCount);
	}

	if (rp_app?.cart) {
		rp_app.cart.item_count = guardCount;
	}
};

cart.getRenderedCartItemsCountFromDom = function () {
	const $inputs = rp$(
		'.cart-listing-module .update-cart-quantity, .cart-listing .update-cart-quantity'
	);
	if ($inputs.length === 0) {
		return null;
	}

	// Desktop + mobile each render an input per line; dedupe by data-id so the
	// checkout guard count matches /ajax/cart itemsCount (not 2x).
	const quantitiesByLineId = {};
	$inputs.each(function () {
		const $input = rp$(this);
		const lineId = $input.data('id');
		if (lineId === undefined || lineId === null || lineId === '') {
			return;
		}
		quantitiesByLineId[String(lineId)] = Number.parseInt($input.val(), 10) || 0;
	});

	const lineIds = Object.keys(quantitiesByLineId);
	if (lineIds.length === 0) {
		return null;
	}

	return lineIds.reduce(function (total, lineId) {
		return total + quantitiesByLineId[lineId];
	}, 0);
};

cart.updateCartPromoMessage = function (cartPromoMessage, cartPromoDisclaimer) {
	const { setCartPromoMessage } = require('public/js/store/product-store');
	setCartPromoMessage(cartPromoMessage, cartPromoDisclaimer);

	// Legacy: updates product_purchase / cart_promo Smarty modules via .cart-promo-message-wrap.
	// Remove once those modules read cart promo from the product store (or are dropped from PDP layouts).
	const $cartPromoWrapper = rp$('.cart-promo-message-wrap');

	if (!$cartPromoWrapper.length) {
		return;
	}

	if (!cartPromoMessage || cartPromoMessage.trim() === '') {
		$cartPromoWrapper.hide();
		return;
	}

	$cartPromoWrapper.show();
	const $cartPromoMessage = $cartPromoWrapper.find('.cart-promo-message');
	if ($cartPromoMessage.length > 0) {
		$cartPromoMessage.html('<i class="fa fa-tag"></i>' + cartPromoMessage);
	} else {
		$cartPromoWrapper.html('<div class="cart-promo-message"><i class="fa fa-tag"></i>' + cartPromoMessage + '</div>');
	}
};

cart.handleCheckoutAction = async function (evt) {
	evt.preventDefault();

	if (cart._checkoutInProgress) {
		return;
	}
	cart._checkoutInProgress = true;

	const $button = rp$(evt.currentTarget);
	const $form = $button.closest('form');
	$button.prop('disabled', true);

	try {
		await utils.canProceedCheckoutWithQuantityLimitEnabled();
	} catch (err) {
		cart._checkoutInProgress = false;
		$button.prop('disabled', false);
		rp_app.logger?.error({
			message: 'Error during checkout quantity limit validation',
			error: err,
		});
		return;
	}

	try {
		if (window.tracking?.google?.use_gtag_admind_analytics) {
			window.rp_app.track.track_ec_general('begin_checkout', {
				currency: rp_app.cart._currency_code,
				items: utils.convertNamesOfItemsForGA(utils.getItemsFromRpappCart(rp_app.cart.cart)),
			});
		}
		trackCheckoutEvent();
	} catch (err) {
		rp_app.logger?.error({
			message: 'Error during checkout tracking',
			error: err,
		});
	}

	$form.submit();
};

cart.SCROLL_STICKY_HEADER_OFFSET = 80;

cart.scrollToExpressCheckout = function (event) {
	event.preventDefault();
	const stickyBar = document.querySelector('.cart-checkout-mobile-sticky');
	const target = stickyBar?.closest('.cart-page-layout')?.querySelector('.cart-subtotal-module') ?? null;
	if (!target) {
		return;
	}
	const scrollTarget = globalThis.scrollY + target.getBoundingClientRect().top - cart.SCROLL_STICKY_HEADER_OFFSET;
	const prefersReducedMotion = globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
	const scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';
	globalThis.scrollTo({ top: scrollTarget, behavior: scrollBehavior });
	const focusTarget = target.querySelector('.checkout-buttons-order-summary');
	focusTarget?.focus({ preventScroll: true });
};

cart.handleKeyupQuantity = function (e) {
	const $input = rp$(this);
	let quantity = parseInt($input.val(), 10);
	const originalQuantity = parseInt($input.data('initial-quantity'), 10);
	// Using parent() to make this somewhat less rigid (as opposed to sibling() or global)
	const $updateLink = $input.parent().find('.update-cart-link');

	const min = parseInt($input.attr('min'), 10) || 0;
	const max = parseInt($input.attr('max'), 10) || PRODUCT_QUANTITY.MAX_QUANTITY_LIMIT;
	if (quantity < min) quantity = min;
	if (quantity > max) quantity = max;

	if (parseInt($input.val(), 10) !== quantity) {
		$input.val(quantity);
		$input.attr('value', quantity);
	}

	// Hide "update" if value is same as original or empty
	if (quantity === originalQuantity || isNaN(quantity)) {
		$updateLink.css({ 'visibility': 'hidden' });
	} else {
		$updateLink.css({ 'visibility': 'visible' });
	}

	if (e.which === 13) {
		cart.updateCart(e);
	}
};

cart.findQuantityInput = function ($button, itemId) {
	let $input = rp$('.update-cart-quantity[data-id="' + itemId + '"]');
	if ($input.length === 0) {
		$input = $button.siblings('.quantity-display, .update-cart-quantity');
	}
	return $input;
};

cart.findRemoveLink = function (itemId, $input) {
	let $removeLink = rp$('.remove-item-link[data-id="' + itemId + '"]');
	if ($removeLink.length === 0) {
		const productId = $input.data('product-id') || $input.data('sku-stripped');
		if (productId) {
			$removeLink = rp$('.remove-item-link[data-product-id="' + productId + '"]');
		}
	}
	
	if ($removeLink.length === 0) {
		$removeLink = $input.closest('.cart-item, .cart-listing-item, tr, .cart-row').find('.remove-item-link');
	}
	
	return $removeLink;
};

cart.calculateNewQuantity = function (currentValue, isIncrease, min, max) {
	if (isIncrease) {
		return currentValue < max ? currentValue + 1 : currentValue;
	}
	return currentValue > min ? currentValue - 1 : currentValue;
};

cart.updateQuantityButtons = function ($input, currentValue, min, max) {
	const $currentIncreaseBtn = $input.siblings('.btn-quantity-increase, .quantity-btn.plus');
	const $currentDecreaseBtn = $input.siblings('.btn-quantity-decrease, .quantity-btn.minus');
	
	if ($currentIncreaseBtn.length > 0) {
		$currentIncreaseBtn.prop('disabled', currentValue >= max);
	}
	if ($currentDecreaseBtn.length > 0) {
		$currentDecreaseBtn.prop('disabled', currentValue <= min);
		
		if (currentValue === 1) {
			$currentDecreaseBtn.attr('data-quantity-one', 'true');
		} else {
			$currentDecreaseBtn.removeAttr('data-quantity-one');
		}
	}
};

cart.toggleUnitPriceVisibility = function ($input, quantity) {
	const $card = $input.closest('.cart-item-card');
	if ($card.length === 0) {
		return;
	}
	const $unitPriceEl = $card.find('.cart-item-total-unit-price');
	const $unitPriceValueEl = $card.find('.cart-item-total-unit-price-value');
	if ($unitPriceEl.length === 0) {
		return;
	}
	if (quantity > 1) {
		$unitPriceEl.removeClass('cart-item-unit-price-hidden');
		const unitPrice = Number($input.data('price'));
		if (!Number.isNaN(unitPrice) && $unitPriceValueEl.length > 0) {
			const unitPriceTemplate = $unitPriceValueEl.text();
			$unitPriceValueEl.text(cart.formatCartPrice(unitPrice, unitPriceTemplate));
		}
	} else {
		$unitPriceEl.addClass('cart-item-unit-price-hidden');
	}
};

cart.updateCartIfNeeded = function ($input, currentValue, min) {
	const originalQuantity = Number.parseInt($input.data('initial-quantity'), 10);
	$input.trigger('keyup');
	
	if (currentValue !== originalQuantity && currentValue >= min) {
		const fakeEvent = {
			preventDefault: function() {},
			target: $input[0]
		};
		cart.updateCart.call($input[0], fakeEvent);
	} else if (cart._cartUpdateInProgress && currentValue >= min) {
		cart._cartUpdateNeedsRetry = true;
	}
};

cart.handleQuantityButton = function (evt) {
	evt.preventDefault();
	const $button = rp$(this);
	const isIncrease = $button.hasClass('btn-quantity-increase') || $button.hasClass('plus');
	const itemId = $button.data('id');
	
	const $input = cart.findQuantityInput($button, itemId);
	if ($input.length === 0) {
		return;
	}

	const min = Number.parseInt($input.attr('min'), 10) || 0;
	const max = Number.parseInt($input.attr('max'), 10) || PRODUCT_QUANTITY.MAX_QUANTITY_LIMIT;
	let currentValue = Number.parseInt($input.val(), 10) || 0;

	if (!isIncrease && currentValue === 1) {
		const $removeLink = cart.findRemoveLink(itemId, $input);
		if ($removeLink.length > 0) {
			const fakeEvent = {
				preventDefault: function() {},
				target: $removeLink[0]
			};
			cart.removeItem.call($removeLink[0], fakeEvent);
			return;
		}
	}

	currentValue = cart.calculateNewQuantity(currentValue, isIncrease, min, max);

	$input.val(currentValue);
	$input.attr('value', currentValue);
	
	cart.updateQuantityButtons($input, currentValue, min, max);
	cart.toggleUnitPriceVisibility($input, currentValue);
	cart.updateCartIfNeeded($input, currentValue, min);
};

cart.sendAdobeDTMCartRemovalEvent = function (product) {
	// Track the removal of the cart product
	const eventDetails = {
		id: String(product.id),
		name: product.name,
		quantity: product.quantity,
		price: product.price,
		brand: product.brand,
		type: product.group || '',
	};

	trackCartRemovalEvent(eventDetails);
};

cart.sendAdobeDTMCartAddEvent = function (product) {
	const eventDetails = {
		id: String(product.id),
		name: product.name,
		quantity: product.quantity,
		price: product.price,
		brand: product.brand,
		type: product.group || '',
	};

	trackCartAddEvent(eventDetails);
};

let shippingQuoteRequestId = 0;

window.getShippingQuote = function (postalCode = null) {
	const requestedPostalCode = postalCode === null ? '' : postalCode.trim();
	shippingQuoteRequestId += 1;
	const requestId = shippingQuoteRequestId;

	rp$(document).trigger('cartGetShippingQuotes:request', [{
		id: requestId,
		postalCode: requestedPostalCode,
	}]);

	/* Fetch Shipping Quote
	.*-----------------------------------------*/
	$ajax({
		method: 'POST',
		dataType: 'json',
		url: '/ajax/cart/shipping-quote',
		data: {
			postalCode: requestedPostalCode === '' ? null : requestedPostalCode,
		},
		success: (response/*, status, xhr*/) => {
			rp$(document).trigger('cartGetShippingQuotes:response', [response, requestId]);
		},
		error: () => {
			rp$(document).trigger('cartGetShippingQuotes:response', [{ status: 'fail' }, requestId]);
		},
	});
};

/**
 * @param {unknown} value
 * @returns {boolean|null} true/false when coercible; null when the flag is present but unrecognized
 */
cart.coerceRpCartWalletFlag = function (value) {
	if (value === true || value === 1) {
		return true;
	}
	if (value === false || value === 0) {
		return false;
	}
	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		if (normalized === 'true' || normalized === '1') {
			return true;
		}
		if (normalized === 'false' || normalized === '0') {
			return false;
		}
	}

	return null;
};

/**
 * True when RP Payments owns a cart-page wallet token (suppresses the matching PayPal wallet button).
 * Prefers explicit wallet keys when present; falls back to enabledWallets for older env payloads.
 *
 * @param {object|undefined} rpPaymentsEnv
 * @param {'apple_pay'|'google_pay'} walletToken
 * @returns {boolean}
 */
cart.isRpCartWalletActive = function (rpPaymentsEnv, walletToken) {
	if (!rpPaymentsEnv || !rpPaymentsEnv.enabled) {
		return false;
	}
	if (Object.hasOwn(rpPaymentsEnv, walletToken)) {
		return cart.coerceRpCartWalletFlag(rpPaymentsEnv[walletToken]) === true;
	}
	const wallets = Array.isArray(rpPaymentsEnv.enabledWallets) ? rpPaymentsEnv.enabledWallets : [];

	return wallets.includes(walletToken);
};

/**
 * Mounts Stripe Express Checkout in the cart subtotal module. Called as early as possible on cart page
 * load so wallet buttons are not blocked by PayPal / legacy wallet SDKs.
 *
 * @param {typeof import('jquery')} rp$
 * @returns {void}
 */
cart.mountCartPageRpExpressCheckout = function (rp$) {
	const rpPaymentsEnv = globalThis.rp_app?.env?.rp_payments;
	if (
		!isRpPaymentsExpressCheckoutEligible(rpPaymentsEnv)
		|| !document.querySelector('#stripe-express-checkout-container')
	) {
		return;
	}

	const estimatedTotal = cart.computeExpressCheckoutEstimatedTotal();
	const amountCents = estimatedTotal === null ? 0 : Math.round(estimatedTotal * 100);

	stripeExpressCheckout.initExpressCheckout('#stripe-express-checkout-container', {
		amountCents,
		loadingCallback: (show) => {
			const $scope = rp$('.cart-subtotal-module');
			const $target = $scope.length > 0 ? $scope : rp$('body');
			if (show) {
				$target.overlaySpinner();
			} else {
				$target.overlaySpinner.remove();
			}
		},
	});
};

$onReady({
	rp$, window,
	label: 'cart',
	fn: function () {
		window.rp_app?.invalidateSupplierCache?.();

		const rpPaymentsEnvEarly = globalThis.rp_app?.env?.rp_payments;
		if (isRpPaymentsExpressCheckoutEligible(rpPaymentsEnvEarly)) {
			stripeExpressCheckout.preloadStripeJsForRpPayments();
			cart.mountCartPageRpExpressCheckout(rp$);
		}

		if (rp$('.cart-listing-module[data-can-apply-cart-quantity-limit]').length && rp_app?.cart) {
			rp_app.cart._can_apply_cart_quantity_limit = true;
		}

		rp$('.cart-listing-module').on('click', '.remove-item-link', cart.removeItem);
		rp$('.cart-listing-module').on('click', '.empty-cart-link', cart.emptyCart);
		rp$('.cart-listing-module').on('click', '.update-cart-link', cart.updateCart);
		rp$('.cart-listing-module').on('click', '.download-list', cart.downloadCSVErrors);
		rp$('.cart-listing-module').on('keyup, change, input', '.update-cart-quantity', cart.handleKeyupQuantity);
		rp$('.cart-listing-module').on('click', '.btn-quantity-increase, .btn-quantity-decrease, .quantity-btn.plus, .quantity-btn.minus', cart.handleQuantityButton);
		rp$('.cart-checkout-component').on('click', '.paypal-button', paypal.doPaypalCheckout);
		rp$('.cart-checkout-component, .cart-checkout-button-component').on('click', '.place-order-button', cart.handleCheckoutAction);

		const expressLink = document.querySelector('.mobile-sticky-express-link');
		if (expressLink) {
			expressLink.addEventListener('click', cart.scrollToExpressCheckout);
		}

		const $cartModal = rp$('#can-proceed-checkout-quantity-limit-enabled');
		if ($cartModal.length && $cartModal.closest('.actions-bar-module').length) {
			rp$('#layout_cart').append($cartModal);
		}

		cartCache.clear();

		rp$('.update-cart-quantity').each(function() {
			const $input = rp$(this);
			const currentValue = Number.parseInt($input.val(), 10) || 0;
			$input.attr('value', currentValue);
			
			const $decreaseBtn = $input.siblings('.btn-quantity-decrease, .quantity-btn.minus');
			if ($decreaseBtn.length > 0) {
				if (currentValue === 1) {
					$decreaseBtn.attr('data-quantity-one', 'true');
				} else {
					$decreaseBtn.removeAttr('data-quantity-one');
				}
			}
		});

		const paypalContainerClass = '.paypal-smart-buttons';
		const rpPaymentsEnv = globalThis.rp_app?.env?.rp_payments;
		const rpPaymentsEnabled = rpPaymentsEnv?.enabled === true;
		const paypalButtonOpts = {
			loadingCallback: (show) => {
				if (show) {
					rp$('body').overlaySpinner();
				} else {
					rp$('body').overlaySpinner.remove();
				}
			}
		};
		const initPaypalSmartButtons = () => {
			if (rpPaymentsEnabled) {
				paypalSmartButtons.initPaypalButton(paypalContainerClass, paypalButtonOpts);
			} else {
				paypalSmartButtons.initSmartButtons(paypalContainerClass, paypalButtonOpts);
			}
		};
		if (rpPaymentsEnabled && typeof globalThis.requestIdleCallback === 'function') {
			globalThis.requestIdleCallback(initPaypalSmartButtons, { timeout: 3000 });
		} else {
			initPaypalSmartButtons();
		}

		// When RP Payments owns cart-page wallets, the Stripe ECE supplies Apple Pay / Google
		// Pay / Link and the PayPal wallet buttons are suppressed; the PayPal smart button stays.
		const rpApplePayEnabled = cart.isRpCartWalletActive(rpPaymentsEnv, 'apple_pay');
		const rpGooglePayEnabled = cart.isRpCartWalletActive(rpPaymentsEnv, 'google_pay');
		if (!rpApplePayEnabled && globalThis.rp_app?.env?.paypal?.apple_pay) {
			applePayButton.initApplePayButton('#paypal-apple-pay-container', {
				loadingCallback: (show) => {
					if (show) {
						rp$('body').overlaySpinner();
					} else {
						rp$('body').overlaySpinner.remove();
					}
				}
			});
		}
		if (!rpGooglePayEnabled && globalThis.rp_app?.env?.paypal?.google_pay) {
			googlePayButton.initGooglePayButton('#paypal-google-pay-container', {
				loadingCallback: (show) => {
					if (show) {
						rp$('body').overlaySpinner();
					} else {
						rp$('body').overlaySpinner.remove();
					}
				},
				environment: globalThis.rp_app?.env?.paypal?.environment === 'sandbox' ? 'TEST' : 'PRODUCTION',
			});
		}
	},
});

if (typeof module !== 'undefined' && module.exports) {
	module.exports = cart;
}

window.cart = cart;
